import { useCallback, useEffect, useState } from "react";
import { Receipt, Plus, TrendUp, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ImageSlot } from "@/components/ImageUpload";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";

const PAGE_SIZE = 10;

function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-MY", { month: "long", year: "numeric" });
}

const MONTH_OPTIONS = (() => {
  const opts = [];
  const d = new Date();
  for (let i = 0; i < 24; i++) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ key, label: monthLabel(key) });
    d.setMonth(d.getMonth() - 1);
  }
  return opts;
})();

export default function ExpensesPanel() {
  const [data, setData] = useState({ expenses: [], total_records: 0, total_amount: 0 });
  const [categories, setCategories] = useState([]);
  const [month, setMonth] = useState(todayLocal().slice(0, 7));
  const [page, setPage] = useState(1);
  const [form, setForm] = useState({ date: todayLocal(), category: "", amount: "", details: "" });
  const [receipt, setReceipt] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => {
    const params = { page, page_size: PAGE_SIZE };
    if (month !== "all") params.month = month;
    api.get("/expenses", { params }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/expense-categories").then((r) => setCategories(r.data)).catch(() => {});
  }, [month, page]);

  useEffect(() => { refresh(); }, [refresh]);

  const changeMonth = (m) => { setMonth(m); setPage(1); };

  const addCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await api.post("/expense-categories", { name: newCategory.trim() });
      toast.success("Category added");
      setNewCategory("");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) { toast.error("Select an expense category"); return; }
    if (!(parseFloat(form.amount) > 0)) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post("/expenses", {
        date: form.date, category: form.category,
        amount: parseFloat(form.amount), details: form.details,
        ...(receipt ? { receipt } : {}),
      });
      toast.success("Expense logged");
      setForm({ date: todayLocal(), category: "", amount: "", details: "" });
      setReceipt(null);
      changeMonth(form.date.slice(0, 7));
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total_records / PAGE_SIZE));

  return (
    <div className="space-y-8" data-testid="expenses-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight">
            <Receipt size={24} className="text-primary" /> Expenses
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">Track OPEX, maintenance and other company spending.</p>
        </div>
        <div className="rounded-sm border border-border bg-card px-5 py-3 text-right" data-testid="expenses-month-total">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground" data-testid="expenses-total-label">
            {month === "all" ? "All Time" : monthLabel(month)}
          </p>
          <p className="font-display text-xl font-black text-primary">RM {(data.total_amount || 0).toFixed(2)}</p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-5 rounded-sm border border-border bg-card p-6" data-testid="expense-form">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Date *</Label>
            <Input data-testid="expense-date" type="date" required value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Category *</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="expense-category" className="rounded-sm border-input bg-background focus:ring-primary">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex gap-2 pt-1">
              <Input data-testid="expense-add-category-input" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
                placeholder="Add new category..." className="rounded-sm border-input bg-background focus-visible:ring-primary" />
              <Button type="button" variant="outline" data-testid="expense-add-category-button" onClick={addCategory}
                className="shrink-0 rounded-sm border-primary/50 text-primary hover:bg-primary/10 hover:text-amber-400">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Amount (RM) *</Label>
            <Input data-testid="expense-amount" type="number" min="0" step="0.01" required value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="rounded-sm border-input bg-background font-mono focus-visible:ring-primary" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Receipt Upload (optional)</Label>
            <div className="max-w-[200px]">
              <ImageSlot label="Receipt Photo" value={receipt} onChange={setReceipt} testid="expense-receipt-upload" />
            </div>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Details</Label>
            <Textarea data-testid="expense-details" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })}
              placeholder="Free-text details — vendor, vehicle, remarks..." rows={2}
              className="rounded-sm border-input bg-background focus-visible:ring-primary" />
          </div>
        </div>
        <Button data-testid="expense-submit" type="submit" disabled={busy}
          className="rounded-sm bg-primary px-8 font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
          {busy ? "Saving..." : "Log Expense"}
        </Button>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Filter by Month</Label>
          <Select value={month} onValueChange={changeMonth}>
            <SelectTrigger data-testid="expense-month-filter" className="w-48 rounded-sm border-input bg-card focus:ring-primary">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="all" data-testid="expense-filter-all">View All</SelectItem>
              {MONTH_OPTIONS.map((o) => <SelectItem key={o.key} value={o.key} data-testid={`expense-filter-${o.key}`}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="expenses-record-count">
          {data.total_records} record{data.total_records === 1 ? "" : "s"}
        </p>
      </div>

      <div className="overflow-x-auto rounded-sm border border-border" data-testid="expenses-table">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Date</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Category</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Details</TableHead>
              <TableHead className="text-right text-xs font-bold uppercase tracking-widest text-muted-foreground">Amount</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Receipt</TableHead>
              <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Logged By</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.expenses.map((x) => (
              <TableRow key={x.id} className="border-border hover:bg-muted/50" data-testid={`expense-row-${x.id}`}>
                <TableCell className="font-mono text-xs">{x.date}</TableCell>
                <TableCell className="text-sm font-semibold">{x.category}</TableCell>
                <TableCell className="max-w-[280px] truncate text-sm text-muted-foreground">{x.details || "—"}</TableCell>
                <TableCell className="text-right font-mono font-bold text-primary">RM {(x.amount ?? 0).toFixed(2)}</TableCell>
                <TableCell>
                  {x.receipt_path ? (
                    <a href={fileUrl(x.receipt_path)} target="_blank" rel="noreferrer" data-testid={`expense-receipt-${x.id}`}
                      className="text-xs font-bold uppercase tracking-wider text-primary hover:text-amber-400 hover:underline">View</a>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{x.logged_by}</TableCell>
              </TableRow>
            ))}
            {!data.expenses.length && (
              <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-muted-foreground">
                <TrendUp size={24} className="mx-auto mb-2 text-muted-foreground" />
                {month === "all" ? "No expenses logged yet." : `No expenses logged for ${monthLabel(month)}.`}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {data.total_records > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="expenses-pagination">
          <p className="text-xs text-muted-foreground" data-testid="expenses-page-info">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" data-testid="expenses-prev-page" disabled={page <= 1}
              onClick={() => setPage(page - 1)} className="rounded-sm border-input hover:bg-secondary hover:text-foreground">
              <CaretLeft size={14} className="mr-1" /> Previous
            </Button>
            <Button variant="outline" size="sm" data-testid="expenses-next-page" disabled={page >= totalPages}
              onClick={() => setPage(page + 1)} className="rounded-sm border-input hover:bg-secondary hover:text-foreground">
              Next <CaretRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
